import { useEffect, useRef, useState } from 'react';

import type { IChannelBudgets, IRecommendedSplit, TTDCampaign } from '@feathr/blackbox';
import { isBudgetExceedsAccountMaxError } from '@feathr/extender/App/EventsPage/CampaignEditPage/AdWizard/AdWizardBudgetStep/AdWizardBudgetStep.utils';
import { DEFAULT_DEBOUNCE_WAIT, useDebounce } from '@feathr/hooks';

/** Per-channel recommendation state the underbudget modal needs. */
export interface IUseChannelBudgetRecommendation {
  /** Backend per-channel/total minimum budgets; null until the request settles. */
  channelMinimums: IChannelBudgets | null;
  /** Backend-recommended split as whole-number percentages. */
  recommendedSplit: IRecommendedSplit | null;
  /** Backend-recommended per-channel dollar amounts at the current budget. */
  recommendedAmounts: { display: number; meta: number } | null;
  /**
   * Whether the numbers are unsettled — in flight, or awaiting a re-query the debounce
   * hasn't started. Blocks the save so a stale split can't be applied.
   */
  isLoading: boolean;
  /** Whether the request failed for a generic reason, leaving nothing to validate against. */
  hasFailed: boolean;
  /** Whether the campaign's minimum exceeds the account's maximum campaign budget. */
  budgetExceedsAccountMax: boolean;
}

interface IUseChannelBudgetRecommendationProps {
  campaign: TTDCampaign;
  /** Only the underbudget recommendation needs this; other types skip the request. */
  enabled: boolean;
}

/**
 * Fetches the per-channel minimums, recommended split, and recommended amounts
 * for the underbudget recommendation modal.
 *
 * The ad wizard gets these by rendering `RecommendedBudget`, which is a card with
 * its own UI. The modal needs the same numbers without that card, so it calls the
 * same `budget_recommendation` endpoint directly.
 *
 * The campaign's edited attributes are sent so the split and minimums reflect the
 * budget the user is typing rather than the persisted value; the endpoint applies
 * them in memory only (it calls `block_save`), so nothing is written. Targetings
 * are deliberately not sent: the minimums are duration/channel-driven and the
 * recommended split is derived from the budget and those minimums, so neither
 * needs the audience — and this modal only ever runs against a published campaign
 * whose targetings are already persisted.
 */
export function useChannelBudgetRecommendation({
  campaign,
  enabled,
}: IUseChannelBudgetRecommendationProps): IUseChannelBudgetRecommendation {
  const [channelMinimums, setChannelMinimums] = useState<IChannelBudgets | null>(null);
  const [recommendedSplit, setRecommendedSplit] = useState<IRecommendedSplit | null>(null);
  const [recommendedAmounts, setRecommendedAmounts] = useState<{
    display: number;
    meta: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [budgetExceedsAccountMax, setBudgetExceedsAccountMax] = useState(false);

  /*
   * `useDebounce` returns a [value, debouncedSetter, setter] tuple — the value only
   * moves when the setter is called, hence the effect below. Same idiom as the
   * wizard's RecommendedBudget.
   */
  const targetValue = campaign.get('exposure_settings')?.target_value ?? 0;
  const [debouncedTargetValue, setDebouncedTargetValue] = useDebounce<number>(
    targetValue,
    DEFAULT_DEBOUNCE_WAIT,
  );
  useEffect(() => {
    setDebouncedTargetValue(targetValue);
  }, [targetValue, setDebouncedTargetValue]);

  /*
   * Tag each request so an out-of-order response can't clobber a newer one —
   * the endpoint has no cancellation and the budget input re-queries as it
   * changes. Same guard the wizard's RecommendedBudget uses.
   */
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || campaign.isPending || campaign.isEphemeral) {
      return;
    }

    async function getRecommendation(): Promise<void> {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const isStale = (): boolean => requestId !== requestIdRef.current;

      try {
        setIsLoading(true);
        const budget = await campaign.getRecommendedBudget({
          campaign: campaign.attributes,
          targetings: [],
        });
        if (isStale()) {
          return;
        }
        setChannelMinimums({
          display: budget.channels.display.minimum,
          meta: budget.channels.meta.minimum,
          total: budget.minimum_budget,
        });
        setRecommendedSplit({
          display: budget.channels.display.percent,
          meta: budget.channels.meta.percent,
        });
        setRecommendedAmounts({
          display: budget.channels.display.amount,
          meta: budget.channels.meta.amount,
        });
        setBudgetExceedsAccountMax(false);
        setHasFailed(false);
      } catch (error) {
        if (isStale()) {
          return;
        }
        /*
         * Either failure leaves the modal without minimums to validate against,
         * so both block the save. They're tracked separately because the wizard's
         * validation shows different copy for each.
         */
        if (isBudgetExceedsAccountMaxError(error)) {
          setBudgetExceedsAccountMax(true);
        } else {
          setHasFailed(true);
        }
      } finally {
        if (!isStale()) {
          setIsLoading(false);
        }
      }
    }

    void getRecommendation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debouncedTargetValue, campaign.id]);

  /* The pre-fill changes the budget, but the re-query waits on the debounce. Values
   * from the old budget are still on screen in that gap, so report it as loading. */
  const isAwaitingRequery = targetValue !== debouncedTargetValue;

  return {
    channelMinimums,
    recommendedSplit,
    recommendedAmounts,
    isLoading: isLoading || isAwaitingRequery,
    hasFailed,
    budgetExceedsAccountMax,
  };
}
